import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { v4 as uuidv4 } from 'uuid';
import { CustomHttpHeaderName } from '../types/http.types';
import { LoadingService } from '../features/shared/loading/loading.service';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  constructor(private loadingService: LoadingService) {}

  intercept(request: HttpRequest<object>, next: HttpHandler): Observable<HttpEvent<object>> {
    const skipInterceptor = request.headers.get(CustomHttpHeaderName.SKIP_INTERCEPTOR)?.toLowerCase() === 'true';
    if (skipInterceptor) {
      // ✅ Pass the request as-is without modifications
      return next.handle(request);
    }

    // it is recommended to use the 'clone' method when changing the request object
    const requestHeaders = this.getHeaders(request);
    const clonedRequest = request.clone({
      // set HTTP headers to be applied on request
      setHeaders: requestHeaders,
      // normalize HTTP request's URL
      url: this.normalizeUrl(request.url),
    });

    // update the global loading state
    const skipLoading = request.headers.get(CustomHttpHeaderName.IGNORE_LOADING);
    if (skipLoading?.toLowerCase() !== 'true') {
      const requestId = requestHeaders[CustomHttpHeaderName.REQUEST_ID];
      if (requestId) {
        this.loadingService.startAction(requestId);
      }
    }

    return next.handle(clonedRequest);
  }

  /**
   * GetHTTP headers to be set on given HTTP request
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getHeaders(request: HttpRequest<any>) {
    const headers: Record<string, string> = {};

    // If this is a translations fetch, skip auth entirely, to avoid CORS issues
    if (request.url.includes('//translations.') || request.url.includes('/dev/i18n/')) {
      return headers;
    }

    const contentTypeHeader = request.headers.get('Content-Type');
    const contentDispositionHeader = request.headers.get('Content-Disposition');

    // set Content-Type if not present
    if (
      !contentTypeHeader &&
      // let the browser set the Content-Type header for file uploads
      !contentDispositionHeader &&
      // do not set content type on DELETE requests (because JSON expects a JSON body)
      request.method !== 'DELETE'
    ) {
      headers['Content-Type'] = 'application/json';
    }

    // set the Request ID header
    headers[CustomHttpHeaderName.REQUEST_ID] = uuidv4();

    return headers;
  }

  /**
   * Set API prefix if necessary
   */
  private normalizeUrl(url: string): string {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    } else {
      return `${environment.apiUrl}${url}`;
    }
  }
}
